import { useMemo, useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Search } from "lucide-react";
import { PERMISSIONS, PERMISSION_MODULES } from "@emr/shared";
import { usePermissionsCatalog, type PermissionCatalogRow } from "@/lib/queries";

interface PermissionMatrixProps {
  selectedIds: Set<string>;
  onChange: (next: Set<string>) => void;
  disabled?: boolean;
}

// Human-friendly module names (default is naive capitalization).
const MODULE_LABELS: Record<string, string> = {
  referringdoctors: "Referring Doctors",
  clinicdoctors: "Clinic Doctors",
  patientinfo: "Patient Info",
  emr: "EMR",
  ai: "AI",
};

interface ModuleGroup {
  module: string;
  label: string;
  permissions: PermissionCatalogRow[];
}

/**
 * Split modules into 3 contiguous chunks, roughly balanced by permission
 * count (contiguous keeps the catalog's display order: column 1 reads
 * patients→…, column 2 continues, column 3 ends at vitals).
 */
function partitionModules(mods: ModuleGroup[]): [ModuleGroup[], ModuleGroup[], ModuleGroup[]] {
  const total = mods.reduce((s, m) => s + m.permissions.length, 0);
  const target = Math.ceil(total / 3);
  const cols: ModuleGroup[][] = [[], [], []];
  let ci = 0;
  let acc = 0;
  for (const m of mods) {
    if (ci < 2 && acc >= target) {
      ci++;
      acc = 0;
    }
    cols[ci].push(m);
    acc += m.permissions.length;
  }
  return [cols[0], cols[1], cols[2]];
}

export function PermissionMatrix({ selectedIds, onChange, disabled }: PermissionMatrixProps) {
  const [search, setSearch] = useState("");
  // null = un-touched → defaults to "first module of each column open".
  // Becomes a concrete set on the first manual toggle.
  const [expanded, setExpanded] = useState<Set<string> | null>(null);
  const { data: catalogData, isLoading } = usePermissionsCatalog();

  const perms = useMemo<PermissionCatalogRow[]>(() => catalogData?.permissions ?? [], [catalogData]);
  const modules = useMemo<ModuleGroup[]>(() => {
    return PERMISSION_MODULES.map((m) => ({
      module: m,
      label: MODULE_LABELS[m] ?? (m.charAt(0).toUpperCase() + m.slice(1)),
      permissions: perms
        .filter((p) => p.module === m)
        .sort((a, b) => a.sortOrder - b.sortOrder),
    })).filter((m) => m.permissions.length > 0);
  }, [perms]);

  const matchingKeys = useMemo(() => {
    if (!search.trim()) return null;
    const q = search.toLowerCase();
    const set = new Set<string>();
    for (const p of perms) {
      if (p.key.toLowerCase().includes(q) || p.label.toLowerCase().includes(q)) {
        set.add(p.key);
      }
    }
    return set;
  }, [search, perms]);

  const filteredModules = useMemo(() => {
    if (!matchingKeys) return modules;
    return modules
      .map((m) => ({ ...m, permissions: m.permissions.filter((p) => matchingKeys.has(p.key)) }))
      .filter((m) => m.permissions.length > 0);
  }, [modules, matchingKeys]);

  // 3-column layout (per user request 2026-09-20): the module list flows
  // into three balanced columns; each column is a vertical stack of
  // expandable modules, so every module header is visible at once instead
  // of scrolling a 24-deep single-column list. Permissions flow vertically
  // within their module's column.
  const columns = useMemo(() => partitionModules(filteredModules), [filteredModules]);

  const isSearching = !!matchingKeys;
  // While searching, force every matching module open so hits are visible.
  const effectiveExpanded = useMemo(() => {
    if (isSearching) return new Set(filteredModules.map((m) => m.module));
    if (expanded) return expanded;
    return new Set(columns.map((c) => c[0]?.module).filter((m): m is string => !!m));
  }, [isSearching, filteredModules, expanded, columns]);

  const columnValue = (col: ModuleGroup[]) =>
    Array.from(effectiveExpanded).filter((k) => col.some((m) => m.module === k));

  const onColumnChange = (col: ModuleGroup[]) => (vals: string[]) => {
    const colKeys = new Set(col.map((m) => m.module));
    const next = new Set<string>();
    for (const k of effectiveExpanded) if (!colKeys.has(k)) next.add(k);
    for (const v of vals) next.add(v);
    setExpanded(next);
  };

  const toggle = (key: string, on: boolean) => {
    if (disabled) return;
    const next = new Set(selectedIds);
    if (on) next.add(key); else next.delete(key);
    onChange(next);
  };

  const selectAllModule = (modPerms: PermissionCatalogRow[], on: boolean) => {
    if (disabled) return;
    const next = new Set(selectedIds);
    for (const p of modPerms) {
      if (on) next.add(p.key); else next.delete(p.key);
    }
    onChange(next);
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-12 rounded-md bg-cream-100/60 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-3 text-muted" />
        <Input
          placeholder="Search permissions by key or label…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-8"
        />
      </div>
      <div className="flex items-center justify-between text-xs text-muted">
        <div>{selectedIds.size} of {PERMISSIONS.length} permissions selected</div>
        {matchingKeys && (
          <Button size="sm" variant="ghost" onClick={() => setSearch("")}>Clear search</Button>
        )}
      </div>
      {/* Scrollable module list — 85 keys across 24 modules overflow any
          screen; the internal scrollbar keeps the surrounding dialog's
          header/footer (incl. Save) always reachable. */}
      <div className="max-h-[55vh] overflow-y-auto pr-1">
        {filteredModules.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted">No permissions match "{search}".</div>
        ) : (
          <div className="grid grid-cols-1 gap-3 items-start md:grid-cols-3">
            {columns.map((col, ci) => (
              <Accordion
                key={ci}
                type="multiple"
                value={columnValue(col)}
                onValueChange={onColumnChange(col)}
                className="space-y-2"
              >
                {col.map((m) => {
                  const allSelected = m.permissions.every((p) => selectedIds.has(p.key));
                  const someSelected = m.permissions.some((p) => selectedIds.has(p.key)) && !allSelected;
                  return (
                    <AccordionItem key={m.module} value={m.module} className="rounded-lg border border-cream-200 px-3 shadow-none">
                      <AccordionTrigger className="hover:no-underline py-3">
                        <div className="flex items-center gap-2 flex-1 pr-2 min-w-0">
                          <Checkbox
                            checked={allSelected ? true : someSelected ? "indeterminate" : false}
                            onCheckedChange={(v) => selectAllModule(m.permissions, v === true)}
                            onClick={(e) => e.stopPropagation()}
                            disabled={disabled}
                          />
                          <span className="font-medium text-sm text-maroon-700 dark:text-amber-500 truncate">{m.label}</span>
                          <Badge variant="outline" className="ml-auto text-[10px] shrink-0">
                            {m.permissions.filter((p) => selectedIds.has(p.key)).length}/{m.permissions.length}
                          </Badge>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pb-3">
                        {/* Vertical flow: permissions stack top-to-bottom inside
                            the module's own column (columns are ~300px wide). */}
                        <div className="space-y-0.5">
                          {m.permissions.map((p) => {
                            const checked = selectedIds.has(p.key);
                            return (
                              <label
                                key={p.key}
                                title={`${p.key} · ${p.action}${p.description ? ` — ${p.description}` : ""}`}
                                className={`flex items-start gap-2 rounded-md p-1.5 text-sm ${checked ? "bg-cream-100" : "hover:bg-cream-50/60"} ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                              >
                                <Checkbox checked={checked} onCheckedChange={(v) => toggle(p.key, v === true)} disabled={disabled} />
                                <div className="min-w-0 flex-1">
                                  <div className="text-xs leading-snug line-clamp-2">{p.label}</div>
                                  <div className="text-[9px] text-muted font-mono truncate">{p.key}</div>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
